import { StepHandle } from "..";
import { WorkflowManager, frameStepService, FrameService, assetService } from "../..";
import { Variant } from "../resource/variant";
import {
    AddonHandle,
    Asset,
    ColorDefinition,
    ColorOption,
    FrameData,
    FrameStepData,
    FrameStepStorage,
    PatternImageData,
    Step,
    VariantResource,
} from "../../types";
import { generateSVGWithUnknownColors } from "../../util/illustration";

export enum FrameStep {
    SelectFrame = "SelectFrame",
    SelectImage = "SelectImage",
    Position = "Position",
}
export class FrameStepHandle extends StepHandle<FrameStepData> {
    public frameService: FrameService;

    constructor(manager: WorkflowManager, step: Step<FrameStepData>, tags: string[]) {
        super(manager, step, tags);
        const frameStepData = (step as Step<FrameStepData>).data;
        this.frameService = new FrameService(frameStepData.forceImageCover, frameStepData.initialZoomLevel);
        this.frameService.connectWorkflowManager(manager, step.stepName);
    }

    selectVariant(variant: Variant) {
        const stepElements = this.manager.getRegionElements(this.step.stepName);
        return frameStepService.selectVariant(this.step, variant.getResource(), stepElements, this.manager, (v) =>
            this.setUpdateState(v),
        );
    }

    onFrameDataChanged(callback: (frameData: FrameData[]) => void) {
        if (!this.frameService) return;

        this.frameService.onFrameDataChanged((frameData) => {
            if (!frameData) return;
            callback(frameData);
        });
    }

    /**
     * Updates the image selection inside the frame.
     * @param asset The asset to use.
     * @param storeAsOriginal Optional: Store this asset as the original, unmodified version of the image. Default: `true`.
     * @param [recalculateOffsets=true] Optional: Recalculates the offsets of the image inside the frame. Default: `true`.
     */
    async selectImage(asset: Asset, storeAsOriginal = true, recalculateOffsets = true): Promise<void> {
        await frameStepService.selectImage(this.step, asset, this.manager, recalculateOffsets);
        if (storeAsOriginal) {
            const existingStorage = this.manager.getStepStorage(this.step.stepName)?.framePatternData || {};
            this.manager.updateStorage(this.step.stepName, {
                framePatternData: {
                    ...existingStorage,
                    originalAssetKey: asset.key,
                    backgroundRemovedAssetKey: undefined,
                    useOriginalAsset: undefined,
                },
            });
        }
    }

    /**
     * Returns `true` if the Background Remover is available for the current session.
     */
    async canUseBackgroundRemover(): Promise<boolean> {
        return this.manager.getClient().canUseAddon(AddonHandle.BackgroundRemover);
    }

    /**
     * Returns `true` if the Background Remover is available for the current session and if the associated frame has a user-supplied raster image.
     */
    async canRemoveBackground(): Promise<boolean> {
        return (
            (await this.canUseBackgroundRemover()) &&
            this.hasOriginalImageSelection() &&
            !!this.frameService?.getImageData()?.src &&
            !this.frameService?.getImageData()?.svg
        );
    }

    /**
     * Removes the background from an image, stores it in the state, and returns the new asset.
     * @param applyNewAsset Optionally applies the new asset as the current image selection. Default: `true`.
     * @returns A promise that resolves with the newly generated Asset.
     */
    async removeBackgroundFromImageSelection(applyNewAsset = true): Promise<Asset> {
        if (!(await this.canUseBackgroundRemover())) {
            throw new Error(
                "The current Integration does not have access to the Background Remover. Please call canUseBackgroundRemover to ensure you disable this feature when appropriate.",
            );
        }
        const image = await this.getOriginalImageSelection();
        if (!image) {
            throw new Error("You must supply an image selection before attempting to remove the background.");
        }
        const bgRemoved = await assetService.removeBackgroundFromAsset(image);
        if (applyNewAsset) {
            await frameStepService.selectImage(this.step, bgRemoved, this.manager, false);
        }
        const existingStorage = this.manager.getStepStorage(this.step.stepName)?.framePatternData || {};
        this.manager.updateStorage(this.step.stepName, {
            framePatternData: {
                ...existingStorage,
                backgroundRemovedAssetKey: bgRemoved.key,
                useOriginalAsset: !applyNewAsset,
            },
        });
        return bgRemoved;
    }

    changeColors(newFills: Map<string, ColorDefinition>) {
        frameStepService.changeColors(this.step, this.manager, newFills);
    }

    getImageData(): PatternImageData | undefined {
        if (!this.frameService) return undefined;
        return this.frameService.getImageData();
    }

    async getColorOption() {
        return this.step.data.colorOption;
    }

    getAvailableColors(): Promise<ColorOption[]> {
        if (!this.step.data.colorPickerEnabled) {
            return Promise.resolve([]);
        }
        return frameStepService.availableColors(this.step, this.manager);
    }

    isColorPickerEnabled() {
        return this.step.data.colorPickerEnabled ?? false;
    }

    async getOriginalImageColors(): Promise<
        | {
              [key: string]: ColorDefinition;
          }
        | undefined
    > {
        const imageData = this.getImageData();
        if (!imageData?.svg) {
            return undefined;
        }
        const svgData = await generateSVGWithUnknownColors(imageData.svg);
        return svgData.colors;
    }

    getMaxAllowedColors(): number | undefined {
        return this.step.data.maxColors;
    }

    getUniqueColorCount(): number {
        return frameStepService.getUniqueColorCount(this.step, this.manager);
    }

    getCurrentFrameStep(
        frameData: FrameData,
        uploading?: any,
        imageUploadComplete?: any,
        variants?: VariantResource[],
    ): FrameStep {
        if (variants && variants.length > 1 && frameData === undefined) {
            return FrameStep.SelectFrame;
        }

        if (uploading || imageUploadComplete || this.getImageData()) {
            return FrameStep.Position;
        }
        return FrameStep.SelectImage;
    }

    getFrameService(): FrameService | undefined {
        return this.frameService;
    }

    hasOverlayImageKey() {
        return this.step.data.overlayImageKey;
    }

    hasOverlayImageUrl() {
        return (this.step.data as any).overlayImageUrl;
    }

    getWhitelistedExtensions() {
        return [
            ...this.step.data.whitelistedExtensions,
            ...(this.step.data.whitelistedExtensions.includes(".jpg") ? [".jpeg"] : []),
        ];
    }

    async getOriginalImageSelection(): Promise<Asset | undefined> {
        const key = this.manager.getStepStorage(this.step.stepName)?.framePatternData?.originalAssetKey;
        if (!key) {
            return undefined;
        }
        return assetService.getLocalOrFromServer(key);
    }

    async getBackgroundRemovedImageSelection(): Promise<Asset | undefined> {
        const key = this.manager.getStepStorage(this.step.stepName)?.framePatternData?.backgroundRemovedAssetKey;
        if (!key) {
            return undefined;
        }
        return assetService.getLocalOrFromServer(key);
    }

    hasOriginalImageSelection(): boolean {
        return !!this.manager.getStepStorage(this.step.stepName)?.framePatternData?.originalAssetKey;
    }

    hasBackgroundRemovedImageSelection(): boolean {
        return !!this.getFrameStepStorage()?.framePatternData?.backgroundRemovedAssetKey;
    }

    getUseOriginalImageSelection(): boolean {
        return this.getFrameStepStorage()?.framePatternData?.useOriginalAsset ?? false;
    }

    async setUseOriginalImageSelection(value: boolean): Promise<void> {
        const existingStorage = this.getFrameStepStorage()?.framePatternData || {};
        if (existingStorage.useOriginalAsset === value) {
            return;
        }
        const originalImage = await this.getOriginalImageSelection();
        if (!originalImage) {
            throw new Error("You must provide an image selection before calling setUseOriginalImageSelection");
        }
        if (!value) {
            const bgRemoved = await this.getBackgroundRemovedImageSelection();
            if (!bgRemoved) {
                throw new Error(
                    "You must call removeBackgroundFromImageSelection before attempting to apply the image.",
                );
            }
            await this.selectImage(bgRemoved, false, false);
        } else {
            await this.selectImage(originalImage, false, false);
        }
        this.manager.updateStorage(this.step.stepName, {
            framePatternData: { ...existingStorage, useOriginalAsset: value },
        });
    }

    private getFrameStepStorage() {
        return this.manager.getStepStorage(this.step.stepName) as FrameStepStorage | undefined;
    }
}
